import type {
  AnnotationGeometry,
  AnnotationType,
  CardImageStatus,
  ImageThumbnailUrls,
} from '@fiszki/shared-contracts';
import { fetchJson, postJson } from '../client';

/**
 * Per backend QueueResponse / QueueItemDto (Fiszki.Api.Api.Study.QueueResponse).
 *
 * `dueAt` jest serializowane przez System.Text.Json jako ISO8601 string z offset'em,
 * frontend trzyma je jako string i konwertuje gdy potrzebne (np. RelativeTimeFormat).
 */

/**
 * Faza 3 (T26) — payload obrazu karty Image dostarczony razem z queue. Mirror backend
 * `Fiszki.Api.Api.Study.QueueItemImageDto` per spec §10.1.
 *
 * **thumbnailUrls:** numeric JSON keys "256"/"512"/"1024" via reuse `ImageThumbnailUrlsDto`
 * (Fix 1 — identyczna wire shape jak `ImageDto.thumbnailUrls`). NULL gdy `status != 'ready'`
 * (worker jeszcze nie wygenerował thumbs); frontend renderuje original jako fallback.
 *
 * **status='failed' visible:** S3 fix — Failed image NIE jest silent-dropped z queue, tylko
 * widoczne z actionable error UI (retry button). Pre-bind `status='pending'` jest filtrowane
 * po backend-side (NIE pojawia się w queue do user'a).
 */
export interface QueueItemImage {
  /** Backend long (Int64). Safe under JS Number.MAX_SAFE_INTEGER. */
  imageId: number;
  /** Read-SAS na container 'originals' (private), 15-min TTL. Always present. */
  originalUrl: string;
  /** NULL gdy status != 'ready' (worker pending/failed). */
  thumbnailUrls: ImageThumbnailUrls | null;
  width: number;
  height: number;
  status: CardImageStatus;
  /** Posortowane po Position ASC (server-side). */
  annotations: {
    /** Backend long. */
    id: number;
    type: AnnotationType;
    geometry: AnnotationGeometry;
    position: number;
    revealLabel: string | null;
    isQuestionSide: boolean;
  }[];
}

export interface QueueItemDto {
  /** Backend long (Int64). Safe under JS Number.MAX_SAFE_INTEGER (2^53). */
  cardId: number;
  variant: string;
  state: string;
  dueAt: string;
  /**
   * Backend `DateTimeOffset?` (ISO8601 z offset) — `null` (logicznie) gdy karta nigdy nie była
   * oceniona (state=New, lazy-created lub świeża). **Wire-format reality:** `HttpResponseExtensions`
   * używa `JsonIgnoreCondition.WhenWritingNull` → backend NIE emituje pola gdy null. Frontend
   * po `JSON.parse` dostaje `undefined` (nie `null`) — stąd typ `?: string | null` (oba przypadki).
   * Konsumer MUSI używać loose null check (`!= null`) lub nullish coalescing, nigdy strict `!== null`.
   *
   * `local-fsrs.predictNextState` używa tego pola jako `last_review` w ts-fsrs Card; bez niego
   * elapsed_days liczy się od `dueAt` (future) i corruption D/S → fałszywe drift'y w
   * `fsrs_local_canonical_drift`.
   */
  lastReviewAt?: string | null;
  difficulty: number;
  stability: number;
  retrievability: number;
  cardType: 'basic' | 'cloze' | 'image' | 'imageText';
  frontContent: string | null;
  backContent: string | null;
  clozeCount: number | null;
  flagReverse: boolean;
  /**
   * ImageText-only orientacja: true → przód=zdjęcie, tył=tekst (BackContent); false → przód=tekst
   * (FrontContent), tył=zdjęcie. Logicznie nieużywane dla basic/cloze/image. **Wire reality:** pole
   * jest OPCJONALNE (backend `bool` default-valued; generated mirror `imageOnFront?: boolean`).
   * Renderer ImageText traktuje `undefined` jak `false`.
   */
  imageOnFront?: boolean;
  /**
   * Cloze-only — gdy true sesja nauki pokazuje pole input dla aktywnej luki (porównanie wpisanej
   * odpowiedzi z body markera). Zawsze false dla basic/image.
   */
  requireTyping: boolean;
  /** Indywidualne wyróżnienie ("gwiazdka") karty przez bieżącego usera (per-card). */
  isStarred?: boolean;
  /**
   * Faza 3 (T26) — image payload dla `cardType='image'`. NULL/undefined dla Basic/Cloze. Backend
   * `JsonIgnoreCondition.WhenWritingNull` → frontend dostaje `undefined` (nie `null`) gdy field
   * jest pusty. Konsumer MUSI używać loose null check (`!= null`).
   */
  image?: QueueItemImage | null;
}

export interface QueueResponse {
  /** Backend long (Int64). Safe under JS Number.MAX_SAFE_INTEGER (2^53). */
  studySessionId: number;
  items: QueueItemDto[];
  remainingNewToday: number;
  remainingDueToday: number;
}

/** `deck` = numeric id albo literal "all" per backend ParseDeckParam (case-sensitive). */
export type DeckSelector = number | 'all';

function deckParam(deck: DeckSelector): string {
  return deck === 'all' ? 'all' : deck.toString();
}

/**
 * Per backend `Fiszki.Api.Api.Study.GradeRequestDto`. Wire format = camelCase per project convention
 * (System.Text.Json `JsonSerializerDefaults.Web`). Spec §4.6 punkt 5 quotes pól w snake_case
 * jako informal naming — actual contract = camelCase (zob. komentarz w GradeRequestDto.cs).
 */
export interface GradeRequestPayload {
  /** Backend long. Safe under JS Number.MAX_SAFE_INTEGER. */
  cardId: number;
  variant: string;
  /** 1=Again, 2=Hard, 3=Good, 4=Easy. */
  rating: 1 | 2 | 3 | 4;
  /** Czas review w ms (cap 1h backend walidator). */
  durationMs: number;
  /** ISO8601 z offset'em (max +5 min skew backend walidator). */
  reviewedAt: string;
  /** Optional GUID — replay zwraca istniejący ReviewId (idempotent). */
  idempotencyKey?: string;
  /** Backend long — z `QueueResponse.studySessionId`. */
  studySessionId: number;
}

/** Per backend `Fiszki.Api.Api.Study.GradeResponse` — canonical FSRS state z C# port'u. */
export interface GradeResponseDto {
  reviewId: number;
  cardId: number;
  variant: string;
  difficulty: number;
  stability: number;
  retrievability: number;
  state: 'New' | 'Learning' | 'Review' | 'Relearning';
  nextDueAt: string;
  intervalDays: number;
}

export interface UndoGradeRequestPayload {
  cardId: number;
  variant: string;
  studySessionId: number;
}

/**
 * Per backend `Fiszki.Api.Api.Study.UndoGradeResponse`. Trzymane ręcznie (mirror DTO, camelCase
 * wire) — generated types służą jako drift-guard, nie są importowane przez endpoints. `undone=false`
 * (+ bieżący/New-defaults state) gdy nie było `Review` do cofnięcia.
 */
export interface UndoGradeResponseDto {
  undone: boolean;
  cardId: number;
  variant: string;
  difficulty: number;
  stability: number;
  retrievability: number;
  state: 'New' | 'Learning' | 'Review' | 'Relearning';
  nextDueAt: string;
}

export const studyApi = {
  getQueue: (deck: DeckSelector) => {
    const params = new URLSearchParams({ deck: deckParam(deck) });
    return fetchJson<QueueResponse>(`/study/queue?${params.toString()}`);
  },
  grade: (payload: GradeRequestPayload) =>
    postJson<GradeResponseDto, GradeRequestPayload>('/study/grade', payload),
  undoGrade: (payload: UndoGradeRequestPayload) =>
    postJson<UndoGradeResponseDto, UndoGradeRequestPayload>('/study/grade/undo', payload),
};
